import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiService } from '../../services/api'
import toast from 'react-hot-toast'
import LoadingSpinner from '../common/LoadingSpinner'
import { NSModal, NSField, NSSelect, NSPrefix } from '../shared/ns-kit'
import { I, Icon } from '../shared/ns-icons'
import { nsConfirm } from '../shared/ns-confirm'
import '../../styles/admin-pages.css'

// ── Helpers ───────────────────────────────────────────────────────────────────
const getDisplayDomain = (domain) => domain?.startsWith('www.') ? domain.substring(4) : domain

const getStatusText = (status) => {
  switch (status) {
    case 'verified':       return 'Verified'
    case 'active':         return 'Active'
    case 'pending':        return 'Pending'
    case 'dns_configured': return 'DNS OK'
    case 'failed':         return 'Failed'
    default:               return status || 'Unknown'
  }
}

const getStatusTone = (status) => {
  if (status === 'verified' || status === 'active') return 'success'
  if (status === 'pending') return 'warning'
  if (status === 'failed') return 'danger'
  return 'info'
}

// ── Toggle (prototype style) ──────────────────────────────────────────────────
function Toggle({ on, onChange }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onChange?.(!on) }}
      style={{
        width: 32, height: 18, borderRadius: 999,
        background: on ? 'var(--accent)' : 'var(--surface-2)',
        position: 'relative', transition: 'background 0.15s ease',
        border: '1px solid var(--border)', flexShrink: 0,
      }}
    >
      <span style={{
        position: 'absolute', top: 1, left: on ? 15 : 1,
        width: 14, height: 14, borderRadius: '50%',
        background: 'white', transition: 'left 0.15s ease',
        boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
      }} />
    </button>
  )
}

// ── SSL cell (prototype style) ────────────────────────────────────────────────
function SslCell({ domain }) {
  const provisioning = domain.ssl_status === 'provisioning'
  const valid = domain.ssl_status === 'valid' || domain.ssl_enabled || domain.verification_method === 'none'
  const days = domain.ssl_expiry_days
  const warning = days != null && days < 60

  if (provisioning) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--info)' }}>
        <Icon name="cloud" className="pulse-dot" />
        Provisioning…
      </div>
    )
  }
  if (!valid) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-muted)' }}>
        <Icon name="shield" />
        No SSL
      </div>
    )
  }
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}>
        <Icon name="shield" style={{ color: warning ? 'var(--warning)' : 'var(--success)' }} />
        <span style={{ color: warning ? 'var(--warning)' : 'var(--text-soft)' }}>
          Valid{days != null ? ` · expires ${days}d` : ''}
        </span>
      </div>
      {warning && <div style={{ fontSize: 10.5, color: 'var(--warning)', marginTop: 2 }}>⚠ SSL expires in {days}d</div>}
    </>
  )
}

// ── DNS Records guide card (prototype style) ──────────────────────────────────
function DnsRecordsCard({ domain }) {
  const fullDomain = domain.subdomain ? `${domain.subdomain}.${domain.domain}` : domain.domain
  const records = domain.dns_records || [
    { type: 'A',     name: '@',          value: '76.76.21.21',         ttl: 'auto', ok: true },
    { type: 'A',     name: '@',          value: '76.76.21.22',         ttl: 'auto', ok: true },
    { type: 'CNAME', name: 'www',        value: 'cname.webnest.app',   ttl: 'auto', ok: false },
    { type: 'TXT',   name: '_webnest',   value: 'verify=8x3kf-2092…',  ttl: 'auto', ok: false },
  ]

  return (
    <div className="card card-pad">
      <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
        <div>
          <h2 className="h2">DNS records · {fullDomain}</h2>
          <div className="text-muted" style={{ fontSize: 12.5, marginTop: 4 }}>Add these to your registrar to complete verification</div>
        </div>
        <span className="badge badge-warning"><span className="badge-dot" /> Awaiting verification</span>
      </div>
      <div className="sites-table" style={{ background: 'var(--bg-2)' }}>
        <div className="row header" style={{ gridTemplateColumns: '0.5fr 1.2fr 2fr 0.6fr auto' }}>
          <div>Type</div><div>Name</div><div>Value</div><div>TTL</div><div>Status</div>
        </div>
        {records.map((r, i) => (
          <div className="row" key={i} style={{ gridTemplateColumns: '0.5fr 1.2fr 2fr 0.6fr auto' }}>
            <div><span className="badge" style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5 }}>{r.type}</span></div>
            <div style={{ fontFamily: 'var(--font-mono)' }}>{r.name}</div>
            <div className="truncate" style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-soft)', fontSize: 12 }}>{r.value}</div>
            <div style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', fontSize: 12 }}>{r.ttl || 'auto'}</div>
            <div>
              {r.ok
                ? <span className="badge badge-success"><I.check /> Detected</span>
                : <span className="badge badge-warning">Awaiting</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Connect domain modal (NS design, 2 steps, wired to /domains) ──────────────
function ConnectDomainModal({ onClose, onConnected }) {
  const [step, setStep] = useState(0)
  const [domain, setDomain] = useState('')
  const [siteId, setSiteId] = useState('')
  const [created, setCreated] = useState(null) // { id, instructions }
  const [copied, setCopied] = useState(null)

  const { data: sitesData } = useQuery('sites', () => apiService.get('/sites').then(r => r.data), { staleTime: 5 * 60 * 1000 })
  const sites = sitesData?.sites || []
  // A domain binds to one specific site (sites.domain_id), so pick the actual site
  const siteOptions = sites.map(s => ({
    value: s.id,
    label: s.company_name ? `${s.company_name} · ${s.name || 'Site'}` : (s.name || 'Site'),
    client_id: s.client_id,
  }))
  const selectedSite = sites.find(s => s.id === siteId)
  React.useEffect(() => { if (!siteId && siteOptions.length) setSiteId(siteOptions[0].value) }, [sites]) // eslint-disable-line react-hooks/exhaustive-deps

  const records = created?.instructions ? Object.values(created.instructions) : []

  const copy = (i, value) => {
    navigator.clipboard?.writeText(value).catch(() => {})
    setCopied(i)
    setTimeout(() => setCopied(null), 1300)
  }

  const createMutation = useMutation(
    () => apiService.post('/domains', { domain: domain.trim().replace(/^https?:\/\//, ''), client_id: selectedSite?.client_id }),
    {
      onSuccess: async (res) => {
        const d = res.data
        const newId = d.domain?.id
        setCreated({ id: newId, instructions: d.verification?.instructions })
        setStep(1)
        // Bind the new domain to the chosen site so it serves THIS site once verified
        if (newId && siteId) {
          try { await apiService.put(`/sites/${siteId}/domain`, { domain_id: newId }) }
          catch (e) { /* non-fatal — can be reassigned from the domain row or site settings */ }
        }
        onConnected?.()
      },
      onError: (e) => toast.error(e.response?.data?.error || e.response?.data?.errors?.[0]?.msg || 'Failed to add domain'),
    }
  )

  const verifyMutation = useMutation(
    () => apiService.post(`/domains/${created.id}/verify`),
    {
      onSuccess: (res) => {
        if (res.data?.domain?.status === 'verified' || res.data?.verification?.verified) toast.success('Domain verified & connected')
        else toast.success(res.data?.message || 'Verification started — DNS may take time to propagate')
        onConnected?.()
        onClose()
      },
      onError: (e) => toast.error(e.response?.data?.error || e.response?.data?.details || 'Verification failed'),
    }
  )

  const submitStep0 = () => {
    if (!domain.trim()) { toast.error('Enter a domain name'); return }
    if (!siteId) { toast.error('Choose a site to connect'); return }
    createMutation.mutate()
  }

  return (
    <NSModal
      icon="domain" tone="accent" onClose={onClose} width={660}
      title="Connect a domain" subtitle={step === 0 ? 'Point a domain you own at a WebNest site.' : 'Add these records at your registrar to verify.'}
      foot={
        step === 0 ? (
          <>
            <span style={{ fontSize: 11.5, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <I.shield style={{ color: 'var(--success)' }} /> Free SSL is issued automatically
            </span>
            <button className="btn" style={{ marginLeft: 'auto' }} onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={submitStep0} disabled={createMutation.isLoading}>
              {createMutation.isLoading ? 'Adding…' : 'Continue'} <I.arrowRight />
            </button>
          </>
        ) : (
          <>
            <button className="btn btn-ghost" style={{ marginRight: 'auto' }} onClick={() => setStep(0)}>
              <I.arrowRight style={{ transform: 'rotate(180deg)' }} /> Back
            </button>
            <button className="btn" onClick={onClose}>I'll do this later</button>
            <button className="btn btn-primary" onClick={() => verifyMutation.mutate()} disabled={verifyMutation.isLoading}>
              <I.cloud /> {verifyMutation.isLoading ? 'Verifying…' : 'Verify & connect'}
            </button>
          </>
        )
      }
    >
      {step === 0 ? (
        <>
          <NSField label="Domain name">
            <NSPrefix prefix="https://" placeholder="example.com" value={domain} onChange={e => setDomain(e.target.value)} />
          </NSField>

          <NSField label="Connect to site">
            <NSSelect value={siteId} onChange={e => setSiteId(e.target.value)} options={siteOptions} />
          </NSField>

          <NSField label="Setup method">
            <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="card card-glow" style={{ padding: 14, border: '1.5px solid var(--border-accent)', background: 'var(--accent-soft)', cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="icon-tile" style={{ width: 28, height: 28, fontSize: 13, background: 'var(--accent)', color: 'white', border: 'none' }}><I.settings /></span>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>Manual DNS</span>
                  <I.check style={{ marginLeft: 'auto', color: 'var(--accent-3)' }} />
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.45 }}>Add records at any registrar (GoDaddy, Namecheap, Cloudflare…).</div>
              </div>
              <div className="card" style={{ padding: 14, border: '1.5px solid var(--border)', background: 'var(--surface)', opacity: 0.6, cursor: 'not-allowed' }} title="Coming soon">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="icon-tile" style={{ width: 28, height: 28, fontSize: 13 }}><I.bolt /></span>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>Buy via WebNest</span>
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.45 }}>Purchase &amp; auto-configure a new domain — coming soon.</div>
              </div>
            </div>
          </NSField>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', borderRadius: 11, background: 'var(--bg-2)', border: '1px solid var(--border)' }}>
            <Icon name="domain" style={{ color: 'var(--accent-3)', fontSize: 16 }} />
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--text)' }}>{domain.replace(/^https?:\/\//, '')}</span>
            <span className="badge badge-warning" style={{ marginLeft: 'auto' }}><span className="badge-dot" /> Awaiting verification</span>
          </div>

          <div className="sites-table" style={{ background: 'var(--bg-2)' }}>
            <div className="row header" style={{ gridTemplateColumns: '0.5fr 1.4fr 2fr 0.6fr auto' }}>
              <div>Type</div><div>Name</div><div>Value</div><div>TTL</div><div></div>
            </div>
            {records.map((r, i) => (
              <div className="row" key={i} style={{ gridTemplateColumns: '0.5fr 1.4fr 2fr 0.6fr auto' }}>
                <div><span className="badge" style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5 }}>{r.type}</span></div>
                <div className="truncate" style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>{r.name}</div>
                <div className="truncate" style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-soft)', fontSize: 12 }}>{r.value}</div>
                <div style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', fontSize: 12 }}>{r.ttl || 'auto'}</div>
                <div>
                  <button className="site-action" title="Copy value" onClick={() => copy(i, r.value)}>
                    {copied === i ? <I.check style={{ color: 'var(--success)' }} /> : <I.link />}
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12, color: 'var(--text-muted)' }}>
            <Icon name="cloud" className="pulse-dot" style={{ color: 'var(--info)' }} />
            Checking DNS automatically — propagation can take up to 24 hours.
          </div>
        </>
      )}
    </NSModal>
  )
}

// ── Assign-to-site card (binds a custom domain to sites.domain_id) ────────────
function AssignSiteCard({ domain, sites, onAssign, isLoading }) {
  const clientSites = sites.filter(s => s.client_id === domain.client_id)
  const currentSite = sites.find(s => s.domain_id === domain.id)
  const [pick, setPick] = useState(currentSite?.id || '')
  React.useEffect(() => { setPick(currentSite?.id || '') }, [domain.id, currentSite?.id])

  const fullName = domain.subdomain ? `${domain.subdomain}.${domain.domain}` : domain.domain
  const dirty = pick !== (currentSite?.id || '')

  const apply = () => {
    if (pick) onAssign(pick, domain.id)
    else if (currentSite) onAssign(currentSite.id, null)
  }

  return (
    <div className="card card-pad">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <span className="icon-tile"><Icon name="domain" /></span>
        <div>
          <h2 className="h2" style={{ marginBottom: 2 }}>Assign to site</h2>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            Choose which site <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-soft)' }}>{fullName}</span> serves.
          </div>
        </div>
      </div>
      {clientSites.length > 0 ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', maxWidth: 520 }}>
          <div style={{ flex: 1 }}>
            <NSSelect
              value={pick}
              onChange={e => setPick(e.target.value)}
              options={[
                { value: '', label: '— Not assigned —' },
                ...clientSites.map(s => ({ value: s.id, label: `${s.name || 'Site'}${s.status ? ` (${s.status})` : ''}` })),
              ]}
            />
          </div>
          <button className="btn btn-primary" style={{ flexShrink: 0 }} onClick={apply} disabled={isLoading || !dirty}>
            {isLoading ? 'Saving…' : 'Apply'}
          </button>
        </div>
      ) : (
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>This client has no sites to assign.</div>
      )}
    </div>
  )
}

const DomainDashboard = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [showConnect, setShowConnect] = useState(false)
  const [statusFilter, setStatusFilter] = useState('all')
  const [selectedDomain, setSelectedDomain] = useState(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const queryClient = useQueryClient()

  // ── Queries & Mutations ────────────────────────────────────────────────────
  const { data: domainsData, isLoading, error, refetch } = useQuery(
    'admin-domains',
    () => apiService.get('/domains').then(res => res.data),
    { staleTime: 30000, refetchInterval: 60000 }
  )

  const { data: sitesData } = useQuery('sites', () => apiService.get('/sites').then(r => r.data), { staleTime: 5 * 60 * 1000 })
  const allSites = sitesData?.sites || []

  const assignToSiteMutation = useMutation(
    ({ siteId, domainId }) => apiService.put(`/sites/${siteId}/domain`, { domain_id: domainId }),
    {
      onSuccess: (_res, vars) => {
        toast.success(vars.domainId ? 'Domain assigned to site' : 'Domain unassigned')
        queryClient.invalidateQueries('admin-domains')
        queryClient.invalidateQueries('sites')
      },
      onError: (e) => toast.error(e.response?.data?.error || 'Failed to assign domain'),
    }
  )

  const handleRefresh = async () => {
    setIsRefreshing(true)
    setTimeout(() => setIsRefreshing(false), 1000)
    try { await refetch() } catch (err) { /* silent */ }
  }

  const verifyDomainMutation = useMutation(
    (domainId) => apiService.post(`/domains/${domainId}/verify`),
    {
      onSuccess: (data) => {
        if (data.data.message && data.data.domain) toast.success(data.data.message)
        else toast.error('Verification response was incomplete')
        queryClient.invalidateQueries('admin-domains')
      },
      onError: (error) => toast.error(error.response?.data?.error || 'Verification failed')
    }
  )

  const deleteDomainMutation = useMutation(
    (domainId) => apiService.delete(`/domains/${domainId}`),
    {
      onSuccess: () => {
        toast.success('Domain deleted successfully!')
        queryClient.invalidateQueries('admin-domains')
        if (selectedDomain?.id === deleteDomainMutation.variables) setSelectedDomain(null)
      },
      onError: (error) => toast.error(error.response?.data?.error || 'Failed to delete domain')
    }
  )

  const updateDomainMutation = useMutation(
    ({ domainId, settings }) => apiService.patch(`/domains/${domainId}`, settings),
    {
      onSuccess: () => {
        toast.success('Domain settings updated!')
        queryClient.invalidateQueries('admin-domains')
      },
      onError: (error) => toast.error(error.response?.data?.error || 'Failed to update domain settings')
    }
  )

  const domains = domainsData?.domains || []

  const filteredDomains = domains.filter(domain => {
    const fullDomainName = domain.subdomain ? `${domain.subdomain}.${domain.domain}` : domain.domain
    const matchesSearch =
      searchTerm === '' ||
      fullDomainName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      domain.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      domain.contact_email?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || domain.status === statusFilter
    return matchesSearch && matchesStatus
  })

  const stats = {
    total:    domains.length,
    active:   domains.filter(d => d.status === 'verified' || d.status === 'active').length,
    expiring: domains.filter(d => d.ssl_expiry_days != null && d.ssl_expiry_days < 60).length,
    pending:  domains.filter(d => d.status === 'pending').length,
  }

  const pendingDomains = filteredDomains.filter(d => d.status === 'pending' || d.status === 'dns_configured')

  const COLS = '2.2fr 1fr 1fr 1.5fr 1.1fr 0.8fr auto'

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 240 }}>
        <LoadingSpinner size="lg" text="Loading domains..." />
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 240, gap: 12, color: 'var(--text-muted)' }}>
        <p style={{ fontSize: 13 }}>Failed to load domains.</p>
        <button className="btn" onClick={() => refetch()}>Retry</button>
      </div>
    )
  }

  return (
    <>
      {/* ── Header ─── */}
      <div className="page-header">
        <div className="page-title">
          <div className="eyebrow">Workspace · Domains</div>
          <h1>Domains</h1>
          <div className="subtitle">{stats.total} connected · {stats.active} active · {stats.pending} pending · {stats.expiring} SSL expiring soon</div>
        </div>
        <div className="flex gap-2 items-center">
          <button className="btn" onClick={handleRefresh} disabled={isRefreshing}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={isRefreshing ? { animation: 'spin 1s linear infinite' } : {}}>
              <path d="M21.5 2v6h-6" /><path d="M2.5 12a10 10 0 0 1 17.8-5.5l1.2 1" />
              <path d="M2.5 22v-6h6" /><path d="M21.5 12a10 10 0 0 1-17.8 5.5l-1.2-1" />
            </svg>
            Refresh
          </button>
          <button className="btn btn-primary" onClick={() => setShowConnect(true)}><I.plus /> Connect domain</button>
        </div>
      </div>

      <div className="page-body">
        {/* ── Mini stats ─── */}
        <div className="bento" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
          {[
            { label: 'Connected', value: stats.total, icon: 'domain', tone: '' },
            { label: 'SSL valid', value: stats.active, icon: 'shield', tone: 'success' },
            { label: 'Expiring < 60d', value: stats.expiring, icon: 'bolt', tone: 'warning' },
            { label: 'Pending', value: stats.pending, icon: 'cloud', tone: 'info' },
          ].map(s => (
            <div key={s.label} className="card card-pad-sm" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <span className={`icon-tile${s.tone ? ' ' + s.tone : ''}`}><Icon name={s.icon} /></span>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</div>
                <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', fontFamily: 'var(--font-mono)', marginTop: 2 }}>{s.value}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Filter bar ─── */}
        <div className="filter-bar">
          <I.search style={{ color: 'var(--text-muted)', fontSize: 16, flexShrink: 0 }} />
          <input
            className="input"
            placeholder="Search domains, clients…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          <div className="vdiv" style={{ height: 22 }} />
          <div className="segmented">
            {[['all', 'All'], ['active', 'Active'], ['verified', 'Verified'], ['pending', 'Pending'], ['failed', 'Failed']].map(([v, l]) => (
              <button key={v} className={statusFilter === v ? 'on' : ''} onClick={() => setStatusFilter(v)}>{l}</button>
            ))}
          </div>
        </div>

        {/* ── Domains table ─── */}
        <div className="sites-table">
          <div className="row header" style={{ gridTemplateColumns: COLS }}>
            <div>Domain</div><div>Client</div><div>Status</div><div>SSL certificate</div><div>Expires</div><div>Auto-renew</div><div></div>
          </div>
          {filteredDomains.length === 0 ? (
            <div className="row" style={{ gridTemplateColumns: '1fr' }}>
              <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-faint)', fontSize: 13 }}>No domains match your filters</div>
            </div>
          ) : filteredDomains.map((domain) => {
            const fullName = domain.subdomain ? `${domain.subdomain}.${domain.domain}` : domain.domain
            const displayName = getDisplayDomain(fullName)
            const isPending = domain.status === 'pending' || domain.status === 'dns_configured'

            return (
              <div
                key={domain.id}
                className="row"
                style={{ gridTemplateColumns: COLS, cursor: 'pointer' }}
                onClick={() => setSelectedDomain(selectedDomain?.id === domain.id ? null : domain)}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Icon name="domain" style={{ color: 'var(--accent-3)' }} />
                    {domain.is_primary && <span className="badge badge-accent" style={{ fontSize: 10 }}>Default</span>}
                    <span style={{ color: 'var(--text)', fontWeight: 500, fontFamily: 'var(--font-mono)' }}>{displayName}</span>
                    {domain.subdomain && <span className="badge" style={{ fontSize: 10 }}>subdomain</span>}
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>→ {fullName}</div>
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--text-soft)' }}>{domain.company_name || '—'}</div>
                <div>
                  <span className={`badge badge-${getStatusTone(domain.status)}`}>
                    <span className="badge-dot" /> {getStatusText(domain.status)}
                  </span>
                </div>
                <div><SslCell domain={domain} /></div>
                <div style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-soft)', fontSize: 12 }}>
                  {domain.expiry_date ? new Date(domain.expiry_date).toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                </div>
                <div onClick={e => e.stopPropagation()}>
                  <Toggle
                    on={!!domain.auto_renew}
                    onChange={(val) => updateDomainMutation.mutate({ domainId: domain.id, settings: { auto_renew: val } })}
                  />
                </div>
                <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
                  {isPending && (
                    <button
                      className="site-action"
                      title="Verify DNS"
                      onClick={() => verifyDomainMutation.mutate(domain.id)}
                      disabled={verifyDomainMutation.isLoading}
                    >
                      <I.settings />
                    </button>
                  )}
                  <button
                    className="site-action"
                    title="Disconnect domain"
                    onClick={async () => {
                      const ok = await nsConfirm({
                        tone: 'danger', icon: 'domain',
                        title: `Disconnect ${displayName}?`,
                        message: 'The domain stops pointing at WebNest. The site stays, but this address will no longer reach it.',
                        detail: 'SSL certificates for this domain will be revoked',
                        confirmLabel: 'Disconnect domain',
                      })
                      if (ok) deleteDomainMutation.mutate(domain.id)
                    }}
                    disabled={deleteDomainMutation.isLoading}
                  >
                    <I.trash />
                  </button>
                </div>
              </div>
            )
          })}
        </div>

        {/* ── Assign selected domain to a specific site ─── */}
        {selectedDomain && (
          <AssignSiteCard
            domain={selectedDomain}
            sites={allSites}
            isLoading={assignToSiteMutation.isLoading}
            onAssign={(siteId, domainId) => assignToSiteMutation.mutate({ siteId, domainId })}
          />
        )}

        {/* ── DNS Records guide for pending domains ─── */}
        {selectedDomain && (selectedDomain.status === 'pending' || selectedDomain.status === 'dns_configured') ? (
          <DnsRecordsCard domain={selectedDomain} />
        ) : pendingDomains.length > 0 ? (
          <DnsRecordsCard domain={pendingDomains[0]} />
        ) : null}
      </div>

      {/* ── Connect domain modal ─── */}
      {showConnect && (
        <ConnectDomainModal
          onClose={() => setShowConnect(false)}
          onConnected={() => { queryClient.invalidateQueries('admin-domains'); queryClient.invalidateQueries('sites') }}
        />
      )}
    </>
  )
}

export default DomainDashboard
